import firebase from 'firebase/compat/app';
import 'firebase/compat/auth';
import constants from '@/constants/backend';
import store from '@/store';
import { ConnectionRefusedError } from '@/util/error';
import { stepUpHeaders, withStepUp } from '@/util/stepUp';

const addAuthorization = async (fetchConfig = {}) => {
  const user = firebase.auth().currentUser;
  if (!user) {
    throw new Error('Nicht angemeldet. Bitte neu einloggen.');
  }

  return {
    ...fetchConfig,
    headers: {
      Authorization: `Bearer ${await user.getIdToken()}`,
      ...(fetchConfig.headers ? fetchConfig.headers : {}),
    },
  };
};

const handleResponse = async (response) => {
  if (response.status === 204) {
    response.data = null;
    return response;
  }
  
  const contentType = response.headers.get('content-type') || '';
  if (contentType.includes('image/')) {
    return response;
  }
  
  const text = await response.text();
  if (!text) {
    response.data = null;
    return response;
  }
  
  try {
    response.data = JSON.parse(text);
  } catch (e) {
    response.data = text;
  }
  
  return response;
};

const NETWORK_RETRY_ATTEMPTS = 3;
const NETWORK_RETRY_DELAY_MS = 400;
const OFFLINE_PROBE_INTERVAL_MS = 5000;

const sleep = (ms) => new Promise((resolve) => {
  setTimeout(resolve, ms);
});

const isFetchNetworkError = (error) => {
  if (!error) return false;
  if (error instanceof ConnectionRefusedError) return true;
  if (error.name === 'AbortError') return false;
  // Browser fetch network failures (proxy down, connection refused, etc.)
  if (error instanceof TypeError) return true;
  return false;
};

let offlineReconnectProbeTimer = null;

const stopOfflineReconnectProbe = () => {
  if (!offlineReconnectProbeTimer) return;
  clearInterval(offlineReconnectProbeTimer);
  offlineReconnectProbeTimer = null;
};

const markBackendOnline = () => {
  stopOfflineReconnectProbe();
  if (store.state.connection.offlineMode) {
    store.commit('connection/setOfflineMode', false);
  }
};

const probeBackendReachable = async () => {
  try {
    const url = `${constants.BACKEND_URL}/admin/login`;
    const response = await fetch(url, { method: 'GET' });
    // Proxy 502/504 still resolve; only treat real backend responses as online.
    if (response.ok || response.status === 401 || response.status === 403) {
      markBackendOnline();
    }
  } catch {
    // still offline
  }
};

const startOfflineReconnectProbe = () => {
  if (offlineReconnectProbeTimer) return;
  offlineReconnectProbeTimer = setInterval(
    probeBackendReachable,
    OFFLINE_PROBE_INTERVAL_MS,
  );
};

const markBackendOffline = () => {
  if (!store.state.connection.offlineMode) {
    store.commit('connection/setOfflineMode', true);
    startOfflineReconnectProbe();
  }
};

const fetchWithNetworkRetry = async (url, config) => {
  let lastError;
  for (let attempt = 0; attempt < NETWORK_RETRY_ATTEMPTS; attempt += 1) {
    try {
      return await fetch(url, config);
    } catch (error) {
      lastError = error;
      if (!isFetchNetworkError(error) || attempt >= NETWORK_RETRY_ATTEMPTS - 1) {
        break;
      }
      await sleep(NETWORK_RETRY_DELAY_MS * (attempt + 1));
    }
  }
  throw lastError;
};

const request = async (path, config = {}) => {
  try {
    const url = path.startsWith(constants.BACKEND_URL)
      ? path
      : `${constants.BACKEND_URL}${path}`;
    const response = await fetchWithNetworkRetry(url, await addAuthorization(config));

    if (response.ok || response.status === 304) {
      markBackendOnline();
    }

    return response;
  } catch (error) {
    if (!isFetchNetworkError(error)) {
      throw error;
    }
    markBackendOffline();
    // eslint-disable-next-line no-console
    console.error('Admin API network error:', path, error);
    throw new ConnectionRefusedError(
      'Backend nicht erreichbar. Läuft das Backend (npm run start:dev im backend-Ordner)?',
    );
  }
};

const getETag = (path) => {
  const etags = JSON.parse(localStorage.getItem('etags')) || {};
  return etags[path];
};
const setETag = (path, etag) => {
  const etags = JSON.parse(localStorage.getItem('etags')) || {};
  const newEtags = {
    ...etags,
    [path]: etag,
  };
  localStorage.setItem('etags', JSON.stringify(newEtags));
};

// Get request are also cached in localStorage with ETags
const getRequest = async (path, params = {}) => {
  const search = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value != null && value !== '') search.set(key, String(value));
  });
  const query = search.toString();
  const fullPath = query ? `${path}?${query}` : path;
  // Get cached data
  const etag = getETag(fullPath);
  const cacheName = 'backend';
  const cache = await caches.open(cacheName);
  const cachedResponse = await cache.match(fullPath);

  const config = {
    ...( // Add ETag if cached
      etag && cachedResponse
        ? { headers: { 'If-None-Match': etag } }
        : undefined
    ),
    method: 'GET',
  };
  try {
    const response = await request(fullPath, config);

    // Etag set, response can be used from cache
    if (response.status === 304 && cachedResponse) {
      return handleResponse(cachedResponse);
    }

    // Cache and save ETag for less network traffic
    if (response.ok && response.headers.has('ETag')) {
      setETag(fullPath, response.headers.get('ETag'));
      cache.put(fullPath, response.clone());
      return handleResponse(response);
    }

    // Cache only for offline mode
    if (response.ok) {
      cache.put(fullPath, response.clone());
    }

    return handleResponse(response);
  } catch (error) {
    // Try to use cached version
    if (cachedResponse) {
      // eslint-disable-next-line no-console
      console.warn(`Backend unreachable. Using cached version for: ${cachedResponse.url}`);

      return handleResponse(cachedResponse);
    }
    throw error;
  }
};

const putRequest = async (path, body, stepUp = null) => {
  const config = {
    method: 'PUT',
    body: JSON.stringify(body),
    headers: {
      'Content-Type': 'application/json',
      ...stepUpHeaders(stepUp),
    },
  };
  return handleResponse(await request(path, config));
};

const patchRequest = async (path, body) => {
  const config = {
    method: 'PATCH',
    body: JSON.stringify(body),
    headers: {
      'Content-Type': 'application/json',
    },
  };
  return handleResponse(await request(path, config));
};

const postRequest = async (path, body, stepUp = null) => {
  const config = {
    method: 'POST',
    body: body === undefined ? undefined : JSON.stringify(body ?? {}),
    headers: {
      'Content-Type': 'application/json',
      ...stepUpHeaders(stepUp),
    },
  };
  return handleResponse(await request(path, config));
};

const postFormRequest = async (path, formData) => {
  const config = {
    method: 'POST',
    body: formData,
  };
  return handleResponse(await request(path, config));
};

const deleteRequest = async (path, stepUp = null) => {
  const config = {
    method: 'DELETE',
    headers: {
      ...stepUpHeaders(stepUp),
    },
  };
  return handleResponse(await request(path, config));
};

const imagePostRequest = async (path, image) => {
  const formData = new FormData();
  formData.append('image', image);
  const config = {
    method: 'POST',
    body: formData,
  };
  return handleResponse(await request(path, config));
};

const postCompanyVariantImages = async (path, { carouselFile, headerFile }) => {
  const formData = new FormData();
  formData.append('imageCarousel', carouselFile);
  formData.append('imageHeader', headerFile);
  const config = {
    method: 'POST',
    body: formData,
  };
  return handleResponse(await request(path, config));
};

const getAuthorizationStatus = async () => getRequest('/admin/login');
const getAccount = async () => getRequest('/account');

const getCompanies = async () => getRequest('/company');
const getCompanyPasswordResetLink = async (companyID) => getRequest(`/company/${companyID}/passwordResetLink`);
const postCompany = async (body) => postRequest('/company', body);
const postCompanyPreflightEmails = async (body) => postRequest('/company/preflight-emails', body);
const postCompanyEmail = async (companyID, body) => postRequest(`/company/${companyID}/email`, body);
const getCompanyLoginEmailStatus = async (companyID) => getRequest(`/company/${companyID}/login-email-status`);
const postCompanyVerifyLoginEmail = async (companyID) => postRequest(`/company/${companyID}/verify-login-email`);
const getCompanyCustomerCare = async (companyID) => getRequest(`/company/${companyID}/customer-care`);
const patchCompanyCustomerCare = async (companyID, body) => patchRequest(`/company/${companyID}/customer-care`, body);
const putCompany = async (companyID, body) => putRequest(`/company/${companyID}`, body);
const postCompanyMenuGenerate = async (companyID, body = {}) => postRequest(`/company/${companyID}/menu/generate`, body);

const postCompanyMenuImportUpload = async (companyID, files) => {
  const formData = new FormData();
  (files || []).forEach((file) => {
    formData.append('menuFiles', file);
  });
  const config = {
    method: 'POST',
    body: formData,
  };
  return handleResponse(await request(`/company/${companyID}/menu/import-upload`, config));
};
const deleteCompany = async (companyID) => withStepUp(
  (stepUp) => deleteRequest(`/company/${companyID}`, stepUp),
);

const getCompanyOwnerPinStatus = async (companyID) => getRequest(`/company/${companyID}/owner-pin/status`);
const postCompanyOwnerPinApprove = async (companyID) => withStepUp(
  (stepUp) => postRequest(`/company/${companyID}/owner-pin/approve`, {}, stepUp),
);
const postCompanyOwnerPinReject = async (companyID) => withStepUp(
  (stepUp) => postRequest(`/company/${companyID}/owner-pin/reject`, {}, stepUp),
);
const putCompanyOwnerPin = async (companyID, body) => withStepUp(
  (stepUp) => putRequest(`/company/${companyID}/owner-pin`, body, stepUp),
);
const deleteCompanyOwnerPin = async (companyID) => withStepUp(
  (stepUp) => deleteRequest(`/company/${companyID}/owner-pin`, stepUp),
);

const getEvents = async () => getRequest('/event');
const postEvent = async (body) => postRequest('/event', body);
const patchEvent = async (eventID, body) => patchRequest(`/event/${eventID}`, body);
const deleteEvent = async (eventID) => patchRequest(`/event/${eventID}`, { deleted: true });
const reviveEvent = async (eventID) => patchRequest(`/event/${eventID}`, { deleted: false });

const getAdminPreparationSettings = async (companyID) => getRequest(
  `/preparation/admin/company/${companyID}/settings`,
);
const putAdminPreparationSettings = async (companyID, body) => putRequest(
  `/preparation/admin/company/${companyID}/settings`,
  body,
);
const putAdminPreparationSpecial = async (companyID, specialID, body) => putRequest(
  `/preparation/admin/company/${companyID}/special/${specialID}`,
  body,
);

const getSpecials = async (companyID) => getRequest(`/special${companyID ? `?companyID=${companyID}` : ''}`);
const postSpecial = async (companyID, body) => postRequest(`/special?companyID=${companyID}`, body);
const putSpecial = async (specialID, body) => putRequest(`/special/${specialID}`, body);
const deleteSpecial = async (specialID) => deleteRequest(`/special/${specialID}`);
const cloneSpecial = async (companyID, specialID, role) => postRequest(
  `/special/${specialID}/clone?companyID=${companyID}`,
  { role },
);

const getBonusByCompany = async (companyID) => getRequest(`/bonus?companyID=${companyID}`);
const patchBonus = async (companyID, bonusID, body) => patchRequest(
  `/bonus/${bonusID}?companyID=${companyID}`,
  body,
);

const getRallys = async () => getRequest('/rally');
const getRally = async (rallyID) => getRequest(`/rally/${rallyID}`);

const getReviews = async (query = '') => getRequest(`/review${query ? `?${query}` : ''}`);
const getRallyParticipationStats = async (rallyID) => getRequest(`/rally/${rallyID}/participation-stats`);
const getRallyMarketingStats = async (rallyID, params = {}) => getRequest(`/rally/${rallyID}/marketing-stats`, params);
const getRallyGallery = async (rallyID, instanceKey) => getRequest(
  `/rally/${rallyID}/gallery`,
  { instanceKey },
);
const postRallyGallery = async (rallyID, instanceKey, file) => {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('instanceKey', instanceKey);
  return handleResponse(await request(`/rally/${rallyID}/gallery`, { method: 'POST', body: formData }));
};
const deleteRallyGalleryImage = async (rallyID, imageId) => deleteRequest(`/rally/${rallyID}/gallery/${imageId}`);
const patchRallyGalleryImage = async (rallyID, imageId, body) => (
  patchRequest(`/rally/${rallyID}/gallery/${imageId}`, body)
);
const rallyGalleryAdminBlobPath = (rallyID, imageId) => (
  `/rally/${rallyID}/gallery/${imageId}/admin-blob`
);
const postRally = async (body) => postRequest('/rally', body);
const patchRally = async (rallyID, body) => patchRequest(`/rally/${rallyID}`, body);
const deleteRally = async (rallyID) => deleteRequest(`/rally/${rallyID}`);
const getRallyTourPushSchedules = async (rallyID) => getRequest(`/rally/${rallyID}/tour-pushes`);
const postRallyTourPushSchedule = async (rallyID, body) => (
  postRequest(`/rally/${rallyID}/tour-pushes`, body)
);
const postCancelRallyTourPushSchedule = async (rallyID, pushId) => (
  postRequest(`/rally/${rallyID}/tour-pushes/${pushId}/cancel`)
);
const putRallyTourPushInstanceRule = async (rallyID, body) => (
  putRequest(`/rally/${rallyID}/tour-pushes/instance-rule`, body)
);
const postTourPlanningPreview = async (body) => postRequest('/tour-guide/planning/preview', body);

const getUsers = async () => getRequest('/user');
const getAdminUser = async (userID) => getRequest(`/user/admin/${userID}`);
const postUser = async (body) => postRequest('/user', body);
const patchUser = async (userID, body) => patchRequest(`/user/${userID}`, body);
const postTicket = async (body) => postRequest('/ticket', body);
const deleteTicket = async (ticketID) => deleteRequest(`/ticket/${ticketID}`);

const getAdmins = async () => getRequest('/admin');
const getAdminProfile = async () => getRequest('/admin/me');
const putAdminNotificationRequest = async (body) => putRequest('/admin/notifications/request', body);
const putAdminNotificationDisable = async () => putRequest('/admin/notifications/disable');
const postAdmin = async (body) => postRequest('/admin', body);
const deleteAdmin = async (adminID) => deleteRequest(`/admin/${adminID}`);

const getModerators = async () => getRequest('/moderator');
const postModerator = async (body) => postRequest('/moderator', body);
const deleteModerator = async (moderatorID) => deleteRequest(`/moderator/${moderatorID}`);

const getTags = async () => getRequest('/tag/all?onlyActive=true');
const postTag = async (body) => postRequest('/tag', body);
const deleteTag = async (tagID) => deleteRequest(`/tag/${tagID}`);
const getCompanyTags = async (companyID) => getRequest(`/tag/company?companyID=${companyID}`);
const setCompanyTags = async (companyID, tagIDs) => postRequest(`/tag/company?companyID=${companyID}`, { tagIDs });

const getDealTemplates = async (onlyActive = true) => getRequest(`/deal-template?onlyActive=${onlyActive}`);
const postDealTemplate = async (body) => postRequest('/deal-template', body);
const putDealTemplate = async (templateId, body) => putRequest(`/deal-template/${templateId}`, body);
const deleteDealTemplate = async (templateId) => deleteRequest(`/deal-template/${templateId}`);

const getDealImagePromptTemplates = async () => getRequest('/deal-image-library/prompt-templates/all');
const postDealImagePromptTemplate = async (body) => postRequest('/deal-image-library/prompt-templates', body);
const putDealImagePromptTemplate = async (templateId, body) => putRequest(`/deal-image-library/prompt-templates/${templateId}`, body);
const deleteDealImagePromptTemplate = async (templateId) => deleteRequest(`/deal-image-library/prompt-templates/${templateId}`);
const searchDealImageLibrary = async (q = '') => getRequest(`/deal-image-library/search?q=${encodeURIComponent(q)}`);
const getDealImageGenerationStatus = async (specialId, draftToken, companyId) => {
  const params = new URLSearchParams();
  if (specialId) params.set('specialId', specialId);
  if (draftToken) params.set('draftToken', draftToken);
  if (companyId) params.set('companyId', companyId);
  return getRequest(`/deal-image-library/generation-status?${params.toString()}`);
};
const postDealImageGenerate = async (body) => postRequest('/deal-image-library/generate', body);
const postDealImageAssign = async (body) => postRequest('/deal-image-library/assign', body);
const postDealImageLibraryUpload = async (formData) => {
  const config = { method: 'POST', body: formData };
  return handleResponse(await request('/deal-image-library/upload', config));
};

const getCompanyLogoGenerationStatus = async (companyId, draftToken) => {
  const params = new URLSearchParams({
    companyId: String(companyId),
    draftToken,
  });
  return getRequest(`/company-logo/generation-status?${params.toString()}`);
};

const getCompanyLogoFontOptions = async () => getRequest('/company-logo/font-options');

const postCompanyLogoGenerate = async ({
  companyId,
  draftToken,
  logoText,
  variant,
  fontStyle,
  referenceFile,
}) => {
  const formData = new FormData();
  formData.append('companyId', String(companyId));
  formData.append('draftToken', draftToken);
  formData.append('variant', variant || 'light');
  if (logoText) formData.append('logoText', logoText);
  if (fontStyle) formData.append('fontStyle', fontStyle);
  if (referenceFile) formData.append('referenceImage', referenceFile);
  const config = { method: 'POST', body: formData };
  return handleResponse(await request('/company-logo/generate', config));
};

const postCompanyLogoAssign = async (body) => postRequest('/company-logo/assign', body);

const getCategories = async (onlyActive = false) => getRequest(`/category/all?onlyActive=${onlyActive}`);
const postCategory = async (body) => postRequest('/category', body);
const putCategory = async (categoryID, body) => putRequest(`/category/${categoryID}`, body);
const deleteCategory = async (categoryID) => deleteRequest(`/category/${categoryID}`);
const getAvailableCategoryIcons = async () => getRequest('/category/icons');
const postCategoryIcon = async (iconFile) => {
  const formData = new FormData();
  formData.append('icon', iconFile);
  const config = {
    method: 'POST',
    body: formData,
  };
  return handleResponse(await request('/category/icon', config));
};

const getRallyCategories = async (onlyActive = false) => (
  getRequest(`/rally-category/all?onlyActive=${onlyActive}`)
);
const postRallyCategory = async (body) => postRequest('/rally-category', body);
const putRallyCategory = async (categoryID, body) => putRequest(`/rally-category/${categoryID}`, body);
const deleteRallyCategory = async (categoryID) => deleteRequest(`/rally-category/${categoryID}`);
const getAvailableRallyCategoryIcons = async () => getRequest('/rally-category/icons');
const postRallyCategoryIcon = async (iconFile) => {
  const formData = new FormData();
  formData.append('icon', iconFile);
  const config = {
    method: 'POST',
    body: formData,
  };
  return handleResponse(await request('/rally-category/icon', config));
};

const getUserSegments = async (onlyActive = false) => (
  getRequest(`/user-segment/all?onlyActive=${onlyActive}`)
);
const postUserSegment = async (body) => postRequest('/user-segment', body);
const putUserSegment = async (segmentID, body) => putRequest(`/user-segment/${segmentID}`, body);
const deleteUserSegment = async (segmentID) => deleteRequest(`/user-segment/${segmentID}`);
const putAdminUserSegments = async (userID, segmentIds) => (
  putRequest(`/user/admin/${userID}/segments`, { segmentIds })
);

const getIntervalTypes = async () => getRequest('/intervalType');
const getEventTags = async () => getRequest('/eventTag');
const getStripeCompanyPackages = async (companyID) => getRequest(`/stripe/payment/data/customer/package?companyID=${companyID}`);

const getPasswordResetLink = async (firebaseID) => getRequest(`/firebaseUser/${firebaseID}/passwordResetLink`);

const getImage = async (imageUrl) => getRequest(imageUrl);
const postImage = async (imageUrl, image) => {
  try {
    const resonse = await imagePostRequest(imageUrl, image);
    return resonse;
  } catch (error) {
    if (error instanceof ConnectionRefusedError) {
      throw new Error('Bild kann im Offlinemodus nicht bearbeitet werden');
    }
    throw error;
  }
};
const deleteImage = async (imageUrl) => {
  try {
    const resonse = await deleteRequest(imageUrl);
    return resonse;
  } catch (error) {
    if (error instanceof ConnectionRefusedError) {
      throw new Error('Bild kann im Offlinemodus nicht geloescht werden');
    }
    throw error;
  }
};

const patchCompanyImagePrimary = async (imageID) => {
  const config = { method: 'PATCH' };
  return handleResponse(await request(`/image/company/${imageID}/primary`, config));
};

const getHobbys = async () => getRequest('/hobby');
const getInterests = async () => getRequest('/interest');

const getProfileChangeMailSettings = async () => getRequest('/profile-change/settings/mail');
const putProfileChangeMailSettings = async (body) => putRequest('/profile-change/settings/mail', body);

const getPushNotificationTemplates = async () => getRequest('/message/admin/push-notification-templates');
const putPushNotificationTemplates = async (body) => putRequest(
  '/message/admin/push-notification-templates',
  body,
);
const getEmailNotificationTemplates = async () => getRequest('/message/admin/email-notification-templates');
const putEmailNotificationTemplates = async (body) => putRequest(
  '/message/admin/email-notification-templates',
  body,
);
const getSystemBroadcastAudienceEstimate = async () => getRequest(
  '/message/admin/system-pushes/audience-estimate',
);
const getSystemBroadcastPushes = async () => getRequest('/message/admin/system-pushes');
const postSystemBroadcastPush = async (body) => postRequest('/message/admin/system-pushes', body);
const postCancelSystemBroadcastPush = async (id) => postRequest(
  `/message/admin/system-pushes/${id}/cancel`,
  {},
);
const getProfileChangeReview = async (token) => getRequest(`/profile-change/review/${token}`);
const approveProfileChangeReview = async (token) => postRequest(`/profile-change/review/${token}/approve`, {});
const rejectProfileChangeReview = async (token, body) => postRequest(`/profile-change/review/${token}/reject`, body);

const getMemberReports = async (query = '') => getRequest(`/member-report${query ? `?${query}` : ''}`);
const getMemberReportReview = async (token) => getRequest(`/member-report/review/${token}`);
const patchMemberReport = async (id, body) => patchRequest(`/member-report/${id}`, body);
const setMemberModerationStatus = async (userId, body) => withStepUp(
  (stepUp) => postRequest(`/member-report/user/${userId}/status`, body, stepUp),
);
const postMemberPermanentBlock = async (userId, body = {}) =>
  postRequest(`/member-report/user/${userId}/permanent-block`, body);
const postAdminCloseUser = async (userId, body) => postRequest(`/user/admin/${userId}/close`, body);
const getMemberReportAppealReview = async (token) => getRequest(`/member-report/appeal/review/${token}`);
const approveMemberReportAppeal = async (token) => postRequest(`/member-report/appeal/review/${token}/approve`, {});
const rejectMemberReportAppeal = async (token, body) => postRequest(`/member-report/appeal/review/${token}/reject`, body);
const getMemberReportReasons = async () => getRequest('/member-report/settings/reasons');
const patchMemberReportReason = async (id, body) => patchRequest(`/member-report/settings/reasons/${id}`, body);

const getSupportThreads = async (query = '') => getRequest(`/support/admin/threads${query ? `?${query}` : ''}`);
const getSupportUnreadSummary = async () => getRequest('/support/admin/unread-summary');
const getSupportThread = async (userID) => getRequest(`/support/admin/threads/${userID}`);
const postSupportThreadMessage = async (userID, { text, image } = {}) => {
  if (image) {
    const formData = new FormData();
    if (text) formData.append('text', text);
    formData.append('image', image);
    return postFormRequest(`/support/admin/threads/${userID}/messages`, formData);
  }
  return postRequest(`/support/admin/threads/${userID}/messages`, { text });
};
const patchSupportThreadRead = async (userID) => patchRequest(`/support/admin/threads/${userID}/read`, {});

const getPromoterPinStatus = async () => getRequest('/promoter/pin');
const putPromoterPin = async (body) => putRequest('/promoter/pin', body);
const getPromoterApplications = async () => getRequest('/promoter/applications');
const getPromoterReview = async (token) => getRequest(`/promoter/applications/review/${token}`);
const approvePromoterReview = async (token, body) => postRequest(`/promoter/applications/review/${token}/approve`, body);
const rejectPromoterReview = async (token, body) => postRequest(`/promoter/applications/review/${token}/reject`, body);
const getPromoters = async () => getRequest('/promoter');
const patchPromoter = async (promoterId, body) => patchRequest(`/promoter/${promoterId}`, body);
const deletePromoter = async (promoterId) => deleteRequest(`/promoter/${promoterId}`);
const getPromoterPayoutRequests = async (params = {}) => getRequest('/promoter/payout/requests', params);
const approvePromoterPayoutRequest = async (requestId, body = {}) => withStepUp(
  (stepUp) => postRequest(`/promoter/payout/requests/${requestId}/approve`, body, stepUp),
);
const rejectPromoterPayoutRequest = async (requestId, body = {}) => postRequest(`/promoter/payout/requests/${requestId}/reject`, body);
const markPromoterPayoutPaid = async (requestId, body = {}) => withStepUp(
  (stepUp) => postRequest(`/promoter/payout/requests/${requestId}/mark-paid`, body, stepUp),
);
const getPromoterPointsSettings = async () => getRequest('/promoter/admin/points-settings');
const putPromoterPointsSettings = async (body) => withStepUp(
  (stepUp) => putRequest('/promoter/admin/points-settings', body, stepUp),
);

const getCrewPinStatus = async () => getRequest('/crew/pin');
const putCrewPin = async (body) => putRequest('/crew/pin', body);
const getCrewApplications = async (params = {}) => getRequest('/crew/applications', params);
const getCrewReview = async (token) => getRequest(`/crew/applications/review/${token}`);
const approveCrewReview = async (token) => postRequest(`/crew/applications/review/${token}/approve`, {});
const rejectCrewReview = async (token, body) => postRequest(`/crew/applications/review/${token}/reject`, body);
const getCrewMembers = async (params = {}) => getRequest('/crew', params);
const deleteCrewMember = async (crewId) => deleteRequest(`/crew/${crewId}`);
const getRallyBlacklist = async (params = {}) => getRequest('/crew/admin/rally-blacklist', params);
const getCrewReports = async (params = {}) => getRequest('/crew/admin/reports', params);

const getPartnerPointsSettings = async () => getRequest('/partner/admin/points-settings');
const putPartnerPointsSettings = async (body) => withStepUp(
  (stepUp) => putRequest('/partner/admin/points-settings', body, stepUp),
);

const getMemberPointsSettings = async () => getRequest('/points/admin/points-settings');
const putMemberPointsSettings = async (body) => withStepUp(
  (stepUp) => putRequest('/points/admin/points-settings', body, stepUp),
);
const postMemberPointsGrant = async (body) => withStepUp(
  (stepUp) => postRequest('/points/admin/grant', body, stepUp),
);

const getInvitationSettings = async () => getRequest('/invitation/admin/settings');
const putInvitationSettings = async (body) => putRequest('/invitation/admin/settings', body);
const getAdminInviteCodes = async () => getRequest('/invitation/admin/codes');
const getAdminInviteCode = async (id) => getRequest(`/invitation/admin/codes/${id}`);
const postAdminInviteCode = async (body) => postRequest('/invitation/admin/codes', body);
const patchAdminInviteCode = async (id, body) => patchRequest(`/invitation/admin/codes/${id}`, body);
const getAdminInviteBatches = async () => getRequest('/invitation/admin/batches');
const postAdminInviteBatch = async (body) => postRequest('/invitation/admin/batches', body);
const getAdminInviteBatch = async (id) => getRequest(`/invitation/admin/batches/${id}`);
const patchAdminInviteBatch = async (id, body) => patchRequest(`/invitation/admin/batches/${id}`, body);
const getAdminInviteBatchCodes = async (id, { offset = 0, limit = 500 } = {}) => (
  getRequest(`/invitation/admin/batches/${id}/codes?offset=${offset}&limit=${limit}`)
);

const getInviteQuotaCampaignAudienceEstimate = async () => getRequest(
  '/invitation/admin/quota-campaigns/audience-estimate',
);
const getInviteQuotaCampaigns = async () => getRequest('/invitation/admin/quota-campaigns');
const postInviteQuotaCampaign = async (body) => postRequest('/invitation/admin/quota-campaigns', body);
const postCancelInviteQuotaCampaign = async (id) => postRequest(
  `/invitation/admin/quota-campaigns/${id}/cancel`,
);

const getAdminRegistrationUnlocks = async (limit = 30) => getRequest(
  `/user/admin/registration-unlocks?limit=${limit}`,
);
const postAdminRegistrationUnlock = async (body) => postRequest('/user/admin/registration-unlocks', body);

const getPartnerInvoices = async (params = {}) => getRequest('/partner/admin/invoices', params);
const patchPartnerInvoiceStatus = async (invoiceId, body) => patchRequest(
  `/partner/admin/invoices/${invoiceId}`,
  body,
);
const getPartnerCommissionSettings = async () => getRequest('/partner/admin/commission-settings');
const putPartnerCommissionSettings = async (body) => withStepUp(
  (stepUp) => putRequest('/partner/admin/commission-settings', body, stepUp),
);

const getUserCompanyVisibilityFilterSettings = async () => getRequest(
  '/partner/admin/user-company-visibility-filter',
);
const putUserCompanyVisibilityFilterSettings = async (body) => putRequest(
  '/partner/admin/user-company-visibility-filter',
  body,
);

const getPotentialDealSavings = async () => postRequest('/special/admin/potential-savings', {});

const getMarketingUsageStats = async (params = {}) => getRequest('/special/admin/marketing-usage', params);

const getPartnerActiveDealLimitSettings = async () => getRequest(
  '/partner/admin/active-deal-limit-settings',
);
const putPartnerActiveDealLimitSettings = async (body) => putRequest(
  '/partner/admin/active-deal-limit-settings',
  body,
);

const getCompanyMapAdminBoosts = async (companyId) => getRequest(`/map-admin-boost/company/${companyId}`);
const getSpecialMapAdminBoosts = async (specialId) => getRequest(`/map-admin-boost/special/${specialId}`);
const postMapAdminBoost = async (body) => postRequest('/map-admin-boost', body);
const putMapAdminBoost = async (id, body) => putRequest(`/map-admin-boost/${id}`, body);
const deleteMapAdminBoost = async (id) => deleteRequest(`/map-admin-boost/${id}`);

const getCompanyAdminHighlights = async (companyId) => getRequest(`/admin-highlight/company/${companyId}`);
const postAdminHighlight = async (body) => postRequest('/admin-highlight', body);
const putAdminHighlight = async (id, body) => putRequest(`/admin-highlight/${id}`, body);
const deleteAdminHighlight = async (id) => deleteRequest(`/admin-highlight/${id}`);

const getCompanyNewBadge = async (companyId) => getRequest(`/company-new-badge/company/${companyId}`);
const putCompanyNewBadge = async (companyId, body) => putRequest(`/company-new-badge/company/${companyId}`, body);
const deleteCompanyNewBadge = async (companyId) => deleteRequest(`/company-new-badge/company/${companyId}`);

export {
  getAuthorizationStatus,
  getAccount,

  getCompanies,
  getCompanyPasswordResetLink,
  getCompanyLoginEmailStatus,
  getCompanyCustomerCare,
  patchCompanyCustomerCare,
  postCompany,
  postCompanyPreflightEmails,
  postCompanyEmail,
  postCompanyVerifyLoginEmail,
  getCompanyOwnerPinStatus,
  postCompanyOwnerPinApprove,
  postCompanyOwnerPinReject,
  putCompanyOwnerPin,
  deleteCompanyOwnerPin,
  deleteCompany,
  putCompany,
  postCompanyMenuGenerate,
  postCompanyMenuImportUpload,

  getEvents,
  postEvent,
  patchEvent,
  deleteEvent,
  reviveEvent,

  getAdminPreparationSettings,
  putAdminPreparationSettings,
  putAdminPreparationSpecial,

  getSpecials,
  postSpecial,
  putSpecial,
  deleteSpecial,
  cloneSpecial,

  getBonusByCompany,
  patchBonus,

  getRallys,
  getRally,
  getRallyParticipationStats,
  getRallyMarketingStats,
  getRallyGallery,
  postRallyGallery,
  patchRallyGalleryImage,
  deleteRallyGalleryImage,
  rallyGalleryAdminBlobPath,
  getReviews,
  postRally,
  patchRally,
  getRallyTourPushSchedules,
  postRallyTourPushSchedule,
  postCancelRallyTourPushSchedule,
  putRallyTourPushInstanceRule,
  deleteRally,
  postTourPlanningPreview,

  getUsers,
  getAdminUser,
  postUser,
  patchUser,

  postTicket,
  deleteTicket,

  getAdmins,
  getAdminProfile,
  putAdminNotificationRequest,
  putAdminNotificationDisable,
  postAdmin,
  deleteAdmin,

  getModerators,
  postModerator,
  deleteModerator,

  getIntervalTypes,
  getEventTags,
  getStripeCompanyPackages,

  getTags,
  postTag,
  deleteTag,
  getCompanyTags,
  setCompanyTags,

  getDealTemplates,
  postDealTemplate,
  putDealTemplate,
  deleteDealTemplate,

  getDealImagePromptTemplates,
  postDealImagePromptTemplate,
  putDealImagePromptTemplate,
  deleteDealImagePromptTemplate,
  searchDealImageLibrary,
  getDealImageGenerationStatus,
  postDealImageGenerate,
  postDealImageAssign,
  postDealImageLibraryUpload,

  getCompanyLogoGenerationStatus,
  getCompanyLogoFontOptions,
  postCompanyLogoGenerate,
  postCompanyLogoAssign,

  getCategories,
  postCategory,
  putCategory,
  deleteCategory,
  getAvailableCategoryIcons,
  postCategoryIcon,

  getRallyCategories,
  postRallyCategory,
  putRallyCategory,
  deleteRallyCategory,
  getAvailableRallyCategoryIcons,
  postRallyCategoryIcon,

  getUserSegments,
  postUserSegment,
  putUserSegment,
  deleteUserSegment,
  putAdminUserSegments,

  getPasswordResetLink,

  getImage,
  postImage,
  postCompanyVariantImages,
  deleteImage,
  patchCompanyImagePrimary,

  getProfileChangeMailSettings,
  putProfileChangeMailSettings,
  getPushNotificationTemplates,
  putPushNotificationTemplates,
  getEmailNotificationTemplates,
  putEmailNotificationTemplates,
  getSystemBroadcastAudienceEstimate,
  getSystemBroadcastPushes,
  postSystemBroadcastPush,
  postCancelSystemBroadcastPush,
  getProfileChangeReview,
  approveProfileChangeReview,
  rejectProfileChangeReview,

  getMemberReports,
  getMemberReportReview,
  patchMemberReport,
  setMemberModerationStatus,
  postMemberPermanentBlock,
  postAdminCloseUser,
  getMemberReportAppealReview,
  approveMemberReportAppeal,
  rejectMemberReportAppeal,
  getMemberReportReasons,
  patchMemberReportReason,

  getSupportThreads,
  getSupportUnreadSummary,
  getSupportThread,
  postSupportThreadMessage,
  patchSupportThreadRead,

  getPromoterPinStatus,
  putPromoterPin,
  getPromoterApplications,
  getPromoterReview,
  approvePromoterReview,
  rejectPromoterReview,
  getPromoters,
  patchPromoter,
  deletePromoter,
  getPromoterPayoutRequests,
  approvePromoterPayoutRequest,
  rejectPromoterPayoutRequest,
  markPromoterPayoutPaid,
  getPromoterPointsSettings,
  putPromoterPointsSettings,

  getCrewPinStatus,
  putCrewPin,
  getCrewApplications,
  getCrewReview,
  approveCrewReview,
  rejectCrewReview,
  getCrewMembers,
  deleteCrewMember,
  getRallyBlacklist,
  getCrewReports,

  getPartnerPointsSettings,
  putPartnerPointsSettings,
  getMemberPointsSettings,
  putMemberPointsSettings,
  postMemberPointsGrant,

  getInvitationSettings,
  putInvitationSettings,
  getAdminInviteCodes,
  getAdminInviteCode,
  postAdminInviteCode,
  patchAdminInviteCode,
  getAdminInviteBatches,
  postAdminInviteBatch,
  getAdminInviteBatch,
  patchAdminInviteBatch,
  getAdminInviteBatchCodes,

  getInviteQuotaCampaignAudienceEstimate,
  getInviteQuotaCampaigns,
  postInviteQuotaCampaign,
  postCancelInviteQuotaCampaign,
  getAdminRegistrationUnlocks,
  postAdminRegistrationUnlock,

  getPartnerInvoices,
  patchPartnerInvoiceStatus,
  getPartnerCommissionSettings,
  putPartnerCommissionSettings,

  getUserCompanyVisibilityFilterSettings,
  putUserCompanyVisibilityFilterSettings,
  getPotentialDealSavings,
  getMarketingUsageStats,

  getPartnerActiveDealLimitSettings,
  putPartnerActiveDealLimitSettings,

  getCompanyMapAdminBoosts,
  getSpecialMapAdminBoosts,
  postMapAdminBoost,
  putMapAdminBoost,
  deleteMapAdminBoost,

  getCompanyAdminHighlights,
  postAdminHighlight,
  putAdminHighlight,
  deleteAdminHighlight,

  getCompanyNewBadge,
  putCompanyNewBadge,
  deleteCompanyNewBadge,

  getHobbys,
  getInterests,
};
